import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Home, Briefcase, ImageIcon, Users, MessageSquare, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import SocialIcons from "@/components/SocialIcons";

const navLinks = [
  { label: "الرئيسية", href: "/", icon: Home },
  { label: "خدماتنا", href: "/services", icon: Briefcase },
  { label: "أعمالنا", href: "/gallery", icon: ImageIcon },
  { label: "من نحن", href: "/about", icon: Users },
  { label: "المدونة", href: "/blog", icon: BookOpen },
  { label: "تواصل معنا", href: "/contact", icon: MessageSquare },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-card/98 backdrop-blur-xl shadow-[0_2px_20px_-4px_hsl(var(--charcoal)/0.12)] border-b border-border"
            : "bg-card/80 backdrop-blur-md"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between py-3 px-4 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/logo.png" alt="حسن عبدالله للمقاولات والديكورات" className="w-10 h-10 object-contain" width={40} height={40} />
            <div className="flex flex-col">
              <span className="text-sm sm:text-base font-bold text-foreground leading-tight">حسن عبدالله</span>
              <span className="text-[10px] sm:text-[11px] text-muted-foreground leading-tight">للمقاولات والديكورات</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="التنقل الرئيسي">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                    isActive
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <SocialIcons size="sm" />
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-gold-dark font-semibold shadow-[0_4px_12px_-2px_hsl(var(--primary)/0.4)] hover:shadow-[0_6px_16px_-2px_hsl(var(--primary)/0.5)] transition-all duration-300"
            >
              <a href="tel:0556641963" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                0556641963
              </a>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold transition-all duration-300"
            >
              <a href="tel:0534859731" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                0534859731
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-foreground p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="القائمة"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden bg-card border-t border-border overflow-hidden"
            >
              <nav className="container mx-auto py-4 px-4 flex flex-col gap-1">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = location.pathname === link.href;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {link.label}
                    </Link>
                  );
                })}
                <div className="flex items-center justify-center gap-3 pt-3 border-t border-border mt-2">
                  <SocialIcons size="md" />
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile Fixed Call Buttons */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.5 }}
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden p-3 bg-card/95 backdrop-blur-xl border-t border-border"
      >
        <div className="flex gap-2">
          <a
            href="tel:0556641963"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm shadow-[0_-2px_16px_-4px_hsl(var(--primary)/0.4)] hover:bg-gold-dark transition-colors"
          >
            <Phone className="w-5 h-5" />
            0556641963
          </a>
          <a
            href="tel:0534859731"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-secondary text-foreground font-semibold text-sm border border-primary/30 hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <Phone className="w-5 h-5" />
            0534859731
          </a>
        </div>
      </motion.div>
    </>
  );
};

export default Header;
